import { applyMiddleware, compose, Middleware, Dispatch, createStore } from "redux";
import { History } from "history";
import { routerMiddleware } from "connected-react-router";
import { composeWithDevTools } from "redux-devtools-extension";
import freeze from "redux-freeze";
import createSagaMiddleware from "redux-saga";
import { showNotification } from "@shared/store/actions";

import { ChatActionTypes } from "../containers/Chat/store/constants";
import appSagas from "./saga";
import rootReducer from "./reducer";

// bug-02 (923-core-context context/engineering/procedure-assistant/bug-fixing/bug-02-giant-toast-on-like.md): action types whose
// SUCCESS payload is a domain object that happens to carry a `.message` field which is NOT a
// user-facing confirmation string -- REACT_TO_MESSAGE_SUCCESS's payload is the full persisted
// ChatMessage, and `.message` there is the answer's own text, not a "Saved!"-style toast body.
// Several other domains (Recognition's create/update/bulk-approve, at least) genuinely rely on this
// middleware's generic `payload.message` toast today via a real confirmation string, so this is a
// narrow denylist rather than a rewrite of the shared convention those still depend on.
const NOTIFICATION_EXCLUDED_ACTION_TYPES: ReadonlyArray<string> = [ChatActionTypes.REACT_TO_MESSAGE_SUCCESS];

const sagaMiddleware = createSagaMiddleware();
let middleware: Array<Middleware>;
// eslint-disable-next-line @typescript-eslint/ban-types
let composer: Function;

if (process.env.NODE_ENV === "development") {
  middleware = [freeze, sagaMiddleware];
  composer = composeWithDevTools({ trace: true, traceLimit: 25 });
} else {
  middleware = [sagaMiddleware];
  composer = compose;
}

export const errorHandlerMiddleware: Middleware = () => (next: Dispatch) => (action) => {
  // Guard `action.payload` (mirrors the SUCCESS branch below): some failures are payload-less by
  // design — e.g. the silent bootstrap `bootstrap.failure()` — and must pass through to the reducer
  // instead of crashing here and swallowing the action.
  if (action.type.includes("FAILURE") && action.payload) {
    next(
      showNotification({
        message: action.payload.error || action.payload.message,
        appearance: "error",
      }),
    );
  }

  if (
    action.type.includes("SUCCESS") &&
    action.payload &&
    action.payload.message &&
    !NOTIFICATION_EXCLUDED_ACTION_TYPES.includes(action.type)
  ) {
    next(
      showNotification({
        message: action.payload.message,
        appearance: "success",
      }),
    );
  }

  return next(action);
};

export default function configureStore(history: History) {
  const store = createStore(
    rootReducer(),
    undefined,
    composer(applyMiddleware(...middleware, routerMiddleware(history), errorHandlerMiddleware)),
  );

  sagaMiddleware.run(appSagas);

  // eslint-disable-next-line
  if ((module as any).hot) {
    // eslint-disable-next-line
    (module as any).hot.accept(() => store.replaceReducer(rootReducer()));
  }

  return { store };
}
